import { useState, useEffect } from 'react';

export interface LatestBook {
  id: string;
  title: string;
  author: string;
  description: string;
  cover: string;
  link: string;
  publishedDate: string;
}

interface Rss2JsonItem {
  title: string;
  link: string;
  thumbnail: string;
  pubDate: string;
  author: string;
  description: string;
  guid: string;
  categories?: string[];
}

interface Rss2JsonResponse {
  status: string;
  items: Rss2JsonItem[];
}

function extractAuthor(title: string, feedAuthor: string): string {
  const match = title.match(/^(.*?)(?:\s*[-–—]\s*|:)/);
  if (match && match[1]) return match[1].trim();
  return feedAuthor || 'Leavitt Peak Press';
}

function stripHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = doc.body.textContent || '';
  return text.replace(/\s+/g, ' ').trim();
}

export function useLatestBook() {
  const [book, setBook] = useState<LatestBook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLatestBook = async () => {
      try {
        setLoading(true);
        setError(null);

        const rssUrl = encodeURIComponent(
          'https://leavittpeakpress.com/feed/'
        );
        const response = await fetch(
          `https://api.rss2json.com/v1/api.json?rss_url=${rssUrl}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const data: Rss2JsonResponse = await response.json();

        if (data.status !== 'ok' || !Array.isArray(data.items)) {
          throw new Error('Invalid response format');
        }

        const bookItems = data.items.filter((item) =>
          item.link.includes('/book/')
        );

        if (bookItems.length === 0) {
          throw new Error('No books found in feed');
        }

        const latest = bookItems[0];
        const author = extractAuthor(latest.title, latest.author);
        const cleanDescription = stripHtml(latest.description);

        setBook({
          id: latest.guid || latest.link,
          title: latest.title,
          author,
          description: cleanDescription.slice(0, 200) + (cleanDescription.length > 200 ? '...' : ''),
          cover: latest.thumbnail || '',
          link: latest.link,
          publishedDate: latest.pubDate,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load latest book');
      } finally {
        setLoading(false);
      }
    };

    fetchLatestBook();
  }, []);

  return { book, loading, error };
}